import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { LayoutGrid, DollarSign, Gift, MessageSquare, CalendarCheck, ShieldCheck, X, Ban, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion, AnimatePresence } from "framer-motion";
import { useCurrency } from "@/hooks/useCurrency";
import { toast } from "sonner";

interface UserDetailModalProps {
  profile: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProfileUpdated?: () => void;
}

interface UserStats {
  cardsCreated: number;
  messagesSigned: number;
  contributionsCount: number;
  totalContributed: number;
  wishlistsCount: number;
  rsvpsCount: number;
}

const UserDetailModal = ({ profile, open, onOpenChange, onProfileUpdated }: UserDetailModalProps) => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [banLoading, setBanLoading] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!profile || !open) return;
    setIsBanned(!!profile.banned_until);
    const fetchStats = async () => {
      setLoading(true);
      const userId = profile.user_id;
      const email = profile.email;

      const [cards, messages, contributions, wishlists, rsvps] = await Promise.all([
        supabase.from("collections").select("id", { count: "exact", head: true }).eq("organizer_id", userId),
        email
          ? supabase.from("messages").select("id", { count: "exact", head: true }).eq("contributor_email", email)
          : { count: 0 },
        email
          ? supabase.from("contributions").select("id, amount", { count: "exact" }).eq("contributor_email", email).eq("status", "completed")
          : { count: 0, data: [] },
        supabase.from("wishlists").select("id", { count: "exact", head: true }).eq("user_id", userId),
        email
          ? supabase.from("rsvps").select("id", { count: "exact", head: true }).eq("guest_email", email)
          : { count: 0 },
      ]);

      const contribData = (contributions as any).data || [];
      const totalContributed = contribData.reduce((sum: number, c: any) => sum + Number(c.amount || 0), 0);

      setStats({
        cardsCreated: cards.count || 0,
        messagesSigned: (messages as any).count || 0,
        contributionsCount: (contributions as any).count || 0,
        totalContributed,
        wishlistsCount: wishlists.count || 0,
        rsvpsCount: (rsvps as any).count || 0,
      });
      setLoading(false);
    };
    fetchStats();
  }, [profile, open]);

  const handleToggleBan = async () => {
    if (!profile) return;
    setBanLoading(true);
    try {
      const res = await supabase.functions.invoke("admin-toggle-user-ban", {
        body: { user_id: profile.user_id, ban: !isBanned },
      });
      if (res.error) throw res.error;
      setIsBanned(!isBanned);
      toast.success(isBanned ? "User account reactivated" : "User account deactivated");
      onProfileUpdated?.();
    } catch (err: any) {
      toast.error(err.message || "Failed to update user status");
    } finally {
      setBanLoading(false);
    }
  };

  if (!profile) return null;

  const content = (
    <UserDetailContent
      profile={profile}
      stats={stats}
      loading={loading}
      isBanned={isBanned}
      banLoading={banLoading}
      onToggleBan={handleToggleBan}
    />
  );

  if (isMobile) {
    return (
      <AnimatePresence>
        {open && (
          <motion.div
            key="user-detail-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
            onClick={() => onOpenChange(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-t-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative"
            >
              {/* Header */}
              <div className="flex items-center py-4 border-b border-border sticky top-0 bg-background rounded-t-2xl z-10 px-4">
                <div className="w-8" />
                <h2 className="text-lg font-bold text-foreground font-serif flex-1 text-center">
                  User Details
                </h2>
                <button
                  onClick={() => onOpenChange(false)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5">
                {content}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">User Details</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};

const UserDetailContent = ({ profile, stats, loading, isBanned, banLoading, onToggleBan }: {
  profile: any; stats: UserStats | null; loading: boolean;
  isBanned: boolean; banLoading: boolean; onToggleBan: () => void;
}) => {
  const currency = useCurrency();
  const initials = (profile.display_name || profile.email || "?")
    .split(" ")
    .map((w: string) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const preferences = [
    { label: "Wants", value: profile.preferences_wants },
    { label: "Already has", value: profile.preferences_has },
    { label: "Style", value: profile.preferences_style },
    { label: "Dislikes", value: profile.preferences_dislikes },
    { label: "Price guidance", value: profile.preferences_price_guidance },
  ].filter(p => p.value);

  const registries = profile.preferences_registries as any[] | null;
  const hasPreferences = preferences.length > 0 || (registries && registries.length > 0);

  return (
    <div className="space-y-4">
      {/* Profile header */}
      <div className="flex items-center gap-4">
        <Avatar className="h-14 w-14">
          <AvatarImage src={profile.avatar_url} />
          <AvatarFallback className="text-lg">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-lg truncate">{profile.display_name || "No name"}</h3>
          <p className="text-sm text-muted-foreground truncate">{profile.email || "No email"}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant={profile._emailVerified ? "default" : "secondary"} className="text-xs">
              <ShieldCheck size={12} className="mr-1" />
              {profile._emailVerified ? "Verified" : "Unverified"}
            </Badge>
            {isBanned && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                <Ban size={12} className="mr-1" /> Disabled
              </Badge>
            )}
            <span className="text-xs text-muted-foreground">
              Joined {format(new Date(profile.created_at), "MMM d, yyyy")}
            </span>
          </div>
        </div>
      </div>


      <Separator />

      {/* Stats grid */}
      <div>
        <h4 className="text-sm font-medium mb-3">Activity</h4>
        {loading ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Loading stats…</p>
        ) : stats ? (
          <div className="grid grid-cols-3 gap-2">
            <StatTile icon={<LayoutGrid size={16} />} label="Cards Created" value={stats.cardsCreated} />
            <StatTile icon={<MessageSquare size={16} />} label="Messages" value={stats.messagesSigned} />
            <StatTile icon={<DollarSign size={16} />} label="Contributed" value={`${currency} ${stats.totalContributed.toFixed(0)}`} />
            <StatTile icon={<DollarSign size={16} />} label="Chip-ins" value={stats.contributionsCount} />
            <StatTile icon={<Gift size={16} />} label="Wishlists" value={stats.wishlistsCount} />
            <StatTile icon={<CalendarCheck size={16} />} label="RSVPs" value={stats.rsvpsCount} />
          </div>
        ) : null}
      </div>

      <Separator />

      {/* Preferences */}
      <div>
        <h4 className="text-sm font-medium mb-3">Gift Preferences</h4>
        {hasPreferences ? (
          <div className="space-y-2">
            {preferences.map(p => (
              <div key={p.label}>
                <span className="text-xs font-medium text-muted-foreground">{p.label}</span>
                <p className="text-sm">{p.value}</p>
              </div>
            ))}
            {registries && registries.length > 0 && (
              <div>
                <span className="text-xs font-medium text-muted-foreground">Registries</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {registries.map((r: any, i: number) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {r.title || r.url || "Registry"}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No preferences set</p>
        )}
      </div>

      <Separator />

      {/* Ban/Unban action */}
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          className={isBanned ? "text-primary" : "text-destructive"}
          onClick={onToggleBan}
          disabled={banLoading}
        >
          {isBanned ? <CheckCircle size={14} className="mr-1.5" /> : <Ban size={14} className="mr-1.5" />}
          {banLoading ? "Processing…" : isBanned ? "Enable Account" : "Disable Account"}
        </Button>
      </div>
    </div>
  );
};

const StatTile = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) => (
  <Card className="p-0">
    <CardContent className="p-3 flex flex-col items-center text-center gap-1">
      <span className="text-muted-foreground">{icon}</span>
      <span className="text-lg font-bold leading-none">{value}</span>
      <span className="text-[10px] text-muted-foreground leading-tight">{label}</span>
    </CardContent>
  </Card>
);

export default UserDetailModal;
